'use client'

import { Cell, Text } from '@vinted/web-ui'
import { useEffect, useState, ReactNode } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { PaymentsIdentityDocumentInfoModel } from '@marketplace-web/pay-identity/payments-identity-data'
import { ErrorItem } from '@marketplace-web/core-api/api-client-util'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import {
  renderValidation,
  useFormValidationMessage,
} from '@marketplace-web/vendor-abstractions/react-hook-form-feature'
import { useAsset } from '@marketplace-web/shared/assets'
import { InformationModal } from '@marketplace-web/business-accounts/business-accounts-ui'
import { useAbTest, useTrackAbTest } from '@marketplace-web/feature-flags/ab-tests-data'

import FileInput from '../../../FileInput'
import { toBase64 } from '../../../../utils'

type Props = {
  fieldName: string
  identifier: string
  title: string
  type: string
  isDisabled: boolean
  mediaTypes?: Array<string>
  maximumFileSize: number
  info?: PaymentsIdentityDocumentInfoModel | null
  apiErrors?: Array<ErrorItem>
  index?: number
}

const SupportingDocumentFile = ({
  fieldName,
  identifier,
  title,
  type,
  apiErrors,
  isDisabled,
  mediaTypes,
  maximumFileSize,
  info,
  index,
}: Props) => {
  const translate = useTranslate('kyc_form.supporting_documents')
  const baseTranslate = useTranslate()
  const asset = useAsset('/assets/kyc')

  const kybInfoTest = useAbTest('kyb_info')
  const isKybInfoAbTestEnabled = kybInfoTest?.variant === 'on'
  useTrackAbTest(kybInfoTest)

  const {
    control,
    register,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useFormContext()

  const validationMessage = useFormValidationMessage(errors)

  const [isInfoModalVisible, setIsInfoModalVisible] = useState(false)

  useEffect(() => {
    register(`${fieldName}.identifier`, { required: true })
    register(`${fieldName}.body`, { required: true })
    register(`${fieldName}.mediaType`, { required: true })

    setValue(`${fieldName}.identifier`, identifier)
  }, [register, setValue, fieldName, identifier])

  useEffect(() => {
    const supportingDocumentErrors = apiErrors?.find(apiError => apiError.field === type)

    if (!supportingDocumentErrors) return

    setError(type, { type: 'manual', message: supportingDocumentErrors.value })
  }, [apiErrors, type, identifier, setError])

  const handleSupportingDocumentChange = async (file: File | null, error?: string) => {
    setValue(`${fieldName}.file`, file)

    if (file) {
      const encodedFile = await toBase64({ file, removeMetadata: true })
      const mediaType = file.type

      setValue(`${fieldName}.body`, encodedFile)
      setValue(`${fieldName}.mediaType`, mediaType)
    }

    if (error) {
      setError(type, { type: 'manual', message: error })
    } else {
      clearErrors(type)
    }

    clearErrors(`${fieldName}.file`)
  }

  const openInfoModal = () => {
    setIsInfoModalVisible(true)
  }

  const closeInfoModal = () => {
    setIsInfoModalVisible(false)
  }

  const inputInformationLink = isKybInfoAbTestEnabled
    ? {
        information_link: (chunks: Array<ReactNode>) => (
          <button type="button" onClick={openInfoModal}>
            <Text as="span" clickable text={chunks} type={Text.Type.Title} />
          </button>
        ),
      }
    : undefined

  const getValidationError = (field: string) => validationMessage(field) || validationMessage(type)

  const renderInfoModal = () => {
    if (!info) return null

    const labels = {
      title: info.title,
      description: info.description,
      closeButtonText: translate('information_modal.buttons.close'),
    }

    return (
      <InformationModal
        {...labels}
        show={isInfoModalVisible}
        iconPath={asset('document.svg')}
        onClose={closeInfoModal}
      />
    )
  }

  return (
    <>
      <Controller
        control={control}
        rules={{ required: baseTranslate('errors.blank', { field: title }) }}
        name={`${fieldName}.file`}
        defaultValue={null}
        render={({ field: { value, name } }) => (
          <FileInput
            index={index}
            disabled={isDisabled}
            acceptedFileTypes={mediaTypes?.join(',')}
            styling={Cell.Styling.Wide}
            value={value}
            title={title}
            name={name}
            maximumFileSize={maximumFileSize}
            isInfoLinkVisible={!!info}
            onChange={handleSupportingDocumentChange}
            translationPrefix="kyc_form.supporting_documents.upload"
            validation={renderValidation(getValidationError(`${fieldName}.file`))}
            inputInformationLink={inputInformationLink}
          />
        )}
      />
      {renderInfoModal()}
    </>
  )
}

export default SupportingDocumentFile
